import React from "react";

import { CADENCE_BADGE_STYLES, CadenceKey } from "./constants";
import CommonModal from "../../Components/Modal";

interface CadenceMapCadence {
  label: string;
  time: string;
  key: string;
  bullets: string[];
}

interface CadenceMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  personaLabel: string;
  personaTagline: string;
  personaIcon: string;
  closingHeadline: string;
  closingBullets: string[];
  cadences: CadenceMapCadence[];
  onSelectCadence: (cadenceKey: string) => void;
}

/**
 * "Rhythm & Outcomes" — matches the prototype's `.cadence-map-modal` exactly:
 * a single continuously-padded box (no header/body divider line), 1040px
 * max width, 16px corners, and the "Click any cadence…" hint shown once,
 * in the bottom legend only.
 */
const CadenceMapModal: React.FC<CadenceMapModalProps> = ({
  isOpen,
  onClose,
  personaLabel,
  personaTagline,
  personaIcon,
  closingHeadline,
  closingBullets,
  cadences,
  onSelectCadence,
}) => {
  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      showHeader={false}
      size="5xl"
      className="rounded-2xl shadow-2xl guidance-rhythm-modal"
      contentClassName="px-7 pt-7 pb-5 overflow-y-auto"
      usePortal
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white bg-purple-600 rounded-full px-3 py-1 uppercase tracking-wide mb-2.5">
            {personaIcon} {personaLabel}
          </span>
          <p className="text-[11px] text-gray-400 mt-0 tracking-wide">
            {personaTagline}
          </p>
          <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mt-3.5 mb-1">
            Cadence map
          </div>
          <h3 className="text-[22px] font-extrabold text-gray-900">
            Your rhythm at a glance
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-gray-400 hover:text-gray-600 text-lg leading-none px-1.5 py-0.5 shrink-0"
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        {cadences.map(cadence => {
          const style =
            CADENCE_BADGE_STYLES[cadence.key as CadenceKey] ||
            CADENCE_BADGE_STYLES.weekly;
          return (
            <button
              key={cadence.key}
              type="button"
              onClick={() => onSelectCadence(cadence.key)}
              className="group flex flex-col text-left border border-gray-200 rounded-[10px] p-3.5 pb-3 bg-[#f8fafc] hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full border"
                  style={{
                    backgroundColor: style.bg,
                    color: style.text,
                    borderColor: style.border,
                  }}
                >
                  {cadence.label}
                </span>
                <span className="text-xs text-gray-400">{cadence.time}</span>
              </div>
              <ul className="space-y-[5px] flex-1">
                {cadence.bullets.map((bullet, idx) => (
                  <li
                    key={idx}
                    className="text-xs text-gray-600 leading-snug flex gap-1.5"
                  >
                    <span className="text-gray-400">•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="text-[11px] font-bold text-[#2563eb] mt-2.5 pt-2 border-t border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity">
                Go to {cadence.label} →
              </div>
            </button>
          );
        })}
      </div>

      <div className="border-t border-gray-100 pt-4">
        <h4 className="text-xl font-extrabold text-gray-900 mb-3">
          {closingHeadline}
        </h4>
        <ul className="space-y-2 mb-0">
          {closingBullets.map((bullet, idx) => (
            <li
              key={idx}
              className="text-[13px] text-gray-700 flex gap-1.5 leading-snug"
            >
              <span className="font-bold text-[#2563eb] shrink-0">✓</span>
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-between text-[11px] text-gray-400 mt-4 pt-3 border-t border-gray-100">
        <span className="italic">Click any cadence to open that playbook.</span>
        <span>AL = Account Level · RL = Relationship Level</span>
      </div>
    </CommonModal>
  );
};

export default CadenceMapModal;
